import { cloneDeep } from "lodash";

import { CreateNodeBody, Node } from "@/@types/ivr";
import { ActionType, NodeType } from "@/app/ivr/bot/utils/enums";
import { nodeFactory } from "@/app/ivr/bot/utils/nodeFactory";
import httpManager from "@/app/shared/utils/httpManager";
import { toFormData } from "@/app/shared/utils/helpers";

class NodeServices {
  private getBaseUrl = (botId?: number) => (botId ? `/ivr/${botId}/nodes` : `/ivr/nodes`);

  async getBotNodes(botId: number): Promise<Node[]> {
    const url = `${this.getBaseUrl(botId)}/`;

    const response = await httpManager.get(url);

    return response.data;
  }

  private async createSingleOne(
    url: string,
    type: NodeType,
    node?: Node
  ): Promise<Node | undefined> {
    const nodeBody = cloneDeep(nodeFactory(type));

    if (!nodeBody) return;

    if (node?.referencial_name) nodeBody.referencial_name = node.referencial_name
    if (node) nodeBody.node = node.id;

    if (type === NodeType.Option) {
      // nodeBody.order = (node?.child.at(-3)?.order || 0) + 1;

      // Se obtiene una lista de los números order que ya están utilizados
      const usedOrders = node?.child.map((child) => child.order) || [];

      // Se encuentra el primer número de orden disponible
      let availableOrder = 1;
      while (usedOrders.includes(availableOrder)) {
        availableOrder++;
      }

      // Asigna el número de orden disponible a nodeBody.order
      nodeBody.order = availableOrder;

      if (node?.option && node.option !== "0") nodeBody.option = node?.option;
    } else if (type == NodeType.OptionConsultApi) {
      nodeBody.order = node?.order;
      nodeBody.option = node?.option;
    }

    console.log(nodeBody);
    console.log(node);
    const response = await httpManager.post(url, nodeBody);

    return response.data;
  }

  private async createWithChildren(url: string, type: NodeType, node?: Node) {
    const createdNode = await this.createSingleOne(url, type, node);

    if (!createdNode?.id) return;

    if (type === NodeType.NumericDialing) {
      await this.createSingleOne(url, NodeType.Option, createdNode);
      await this.createSingleOne(url, NodeType.WrongOption, createdNode);
      await this.createSingleOne(url, NodeType.ExpiredTime, createdNode);
    }

    console.log(type, 'type create')
    if (type === NodeType.VariableCapture) {
      await this.createSingleOne(url, NodeType.ExpiredTime, createdNode);
    }
  }

  private async createOptionConsultAPI(botId: number, type: NodeType, node: Node, order?: number, option_value?: string) {
    const url = `${this.getBaseUrl(botId)}/`;

    if (type == NodeType.OptionConsultApi && order) {
      if (option_value == 'false')
        node.option = '0';  
      else if (option_value == 'true')
        node.option = '1';
      else
        node.option = String(option_value);

      node.order = order;
      let referencial_name = ''
      if (option_value == 'true') referencial_name = 'Document - Verdadero'
      else if (option_value == 'false') referencial_name = 'Document - False'
      else referencial_name = 'Document - Opción ' + option_value

      console.log(referencial_name, 'referencial_name');
      node.referencial_name = referencial_name;
    }
    await this.createSingleOne(url, type, node);
  }

  async create(botId: number, type: NodeType, node?: Node) {
    const url = `${this.getBaseUrl(botId)}/`;

    await this.createWithChildren(url, type, node);
  }

  async getDetails(nodeId: number) {
    const url = `${this.getBaseUrl()}/${nodeId}/`;

    const response = await httpManager.get(url);

    return response.data;
  }

  async getParentNodes(nodeId: number) {
    const url = `${this.getBaseUrl()}/${nodeId}/parents/`;

    const response = await httpManager.get(url);

    return response.data;
  }

  async delete(node: Node | undefined, parentNode?: Node) {
    if (!node) return;

    const url = `${this.getBaseUrl()}/${node.id}/`;

    await httpManager.delete(url);

    if (!parentNode) return;

    await this.update(parentNode, { action: ActionType.EndCall }, { preventDefault: true });
  }

  async update(node: Node, newNodeBody: Partial<CreateNodeBody>, { preventDefault = false } = {}, dataValue = []) {
    const url = `${this.getBaseUrl()}/${node.id}/`;

    const validatedNewNode = cloneDeep(newNodeBody);

    // Se obtienen las propiedades compartidas entre node y validatedNewNode
    const sharedProps = Object.keys(node).filter((prop) =>
      Object.prototype.hasOwnProperty.call(validatedNewNode, prop)
    );

    // Se eliminan las propiedades con valor null o undefined en validatedNewNode
    sharedProps.forEach((prop: string) => {
      if (prop.includes("audio")) {
        const nodePropValue = node[prop as keyof Node];
        const newNodePropValue = validatedNewNode[prop as keyof CreateNodeBody];

        if (
          (typeof nodePropValue === "string" && newNodePropValue === null) ||
          newNodePropValue instanceof Blob
        ) {
          return;
        }

        delete validatedNewNode[prop as keyof CreateNodeBody];
      }
    });

    for (const key in validatedNewNode) {
      const currentKey = key as keyof CreateNodeBody;

      const isNotAudioKeyAndIsNull =
        !key.includes("audio") &&
        (validatedNewNode[currentKey] === null || validatedNewNode[currentKey] === undefined);

      const isAudioKeyAndIsString =
        key.includes("audio") && typeof newNodeBody[currentKey] === "string";

      if (isNotAudioKeyAndIsNull || isAudioKeyAndIsString) {
        delete validatedNewNode[currentKey];
      }
    }

    // Solo enviar queue y portfolio para los casos de transferencia a cola y envío plantilla whatsapp.
    if (
      newNodeBody.action !== ActionType.TransferToQueue &&
      newNodeBody.action !== ActionType.SendWppTemplate &&
      newNodeBody.max_attempts_action !== ActionType.TransferToQueue
    ) {
      validatedNewNode.queue = null;
      validatedNewNode.portfolio = null;
    }

    const formDataBody = toFormData(validatedNewNode, { avoidEmpty: false });

    // Se establece el valor de has_validation en false porque se esta enviando vacío
    formDataBody.set("has_validation", formDataBody.get("has_validation") || "false");

    await httpManager.put(url, formDataBody);

    if (preventDefault) return;

    let newNodeType: NodeType | undefined = undefined;

    console.log(dataValue, 'dataValue');
    if (node.action === newNodeBody.action && newNodeBody.action != ActionType.ConnectorApi) return;

    /* ----- ✅ ZONA DE ASIGNACIÓN DE TIPO DE NODO -----
    Se asigna el tipo de nodo correspondiente a la acción seleccionada */

    switch (newNodeBody.action) {
      case ActionType.NumericDialing:
        newNodeType = NodeType.NumericDialing;
        break;
      case ActionType.VariableCapture:
        newNodeType = NodeType.VariableCapture;
        break;
      case ActionType.SatisfactionRanking:
        newNodeType = NodeType.SatisfactionRanking;
        break;
      default:
        break;
    }

    // Se crean los nodos hijos para los que sean NumericDialing o VariableCapture
    if (newNodeType) await this.create(node.ivr, newNodeType, node);

    console.log(newNodeBody, 'new node body');
    console.log(node, 'node body');
    // Si la nueva accion es CONNECTOR API
    if (newNodeBody.action == ActionType.ConnectorApi) {
      // Y la nueva accion es diferente a la anterior se crean las opciones y una opcion incorrecta de api
      if (newNodeBody.action != node.action) {
        if (dataValue)
          await dataValue.forEach(async (el, pos) => {
            console.log(el, 'element dataValue');
            await this.createOptionConsultAPI(node.ivr, NodeType.OptionConsultApi, { ...node, action: ActionType.ConnectorApi }, pos + 1, el);
          })
        
        await this.createOptionConsultAPI(node.ivr, NodeType.WrongOptionApi, node);
      }

      // Y la nueva accion es la misma de la anterior, se verifica si cambio el connector api
      else if (newNodeBody.api_connector != node.api_connector?.id) {

        console.log('entra aca')
        console.log(dataValue, 'data value');
        // borramos las opciones anterior y creamos otras de nuevo
        for (const el of node.child) {
          if (![NodeType.ExpiredTime, NodeType.WrongOptionApi].includes(el.type as NodeType)) {
            await this.delete(el);
          }
        }

        if (dataValue)
          await dataValue.forEach(async (el, pos) => {
            console.log(el, 'element dataValue');
            await this.createOptionConsultAPI(node.ivr, NodeType.OptionConsultApi, { ...node, action: ActionType.ConnectorApi }, pos + 1, el);
          })
      }
    }


    /* -----❗❗ ZONA DE ELIMINACIÓN DE NODOS❗❗-----
    Se eliminan los nodos hijos si la acción del nodo cambia*/
    if (node.action !== newNodeBody.action && newNodeBody.action !== undefined) {
      const firstChildIsExpiredTime = node.child.at(0)?.type === NodeType.ExpiredTime;

      // Para IVRs que inician con capturar variable
      if (
        node.parent_type === null &&
        node.type === NodeType.VariableCapture &&
        node.child.length === 2
      ) {
        console.log('entra a eliminar 1', node.child.at(0))
        await this.delete(node.child.at(0));
      }

      // Si la accion anterior es CONNECTOR API se deben borrar las opciones y opcion incorrecta de API
      if (node.action == ActionType.ConnectorApi) {
        for (const el of node.child) {
          if (el.type !== NodeType.ExpiredTime) {
            console.log('entra a eliminar 2', el);
            await this.delete(el);
          }
        }
        return;
      }

      if (node.parent_type === null) return;
      if (node.parent_type !== null && firstChildIsExpiredTime) return;

      await this.delete(node.child.at(0));
      return;
    }
  }
}

export const nodeServices = new NodeServices();
